import CloseIcon from "@mui/icons-material/Close";
import {
  Backdrop,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid2,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../views/components/common/textFields/TextField";
import {
  PARTNER_TAG_ID_MAX_LENGTH,
  PARTNER_TAG_NAME_MAX_LENGTH,
  PartnerCustomerInventoryTag,
  partnerTagIdFromName,
} from "../../../../../models/inventory/PartnerCustomerInventoryTag";

interface CreateInventoryTagPopupProps {
  open: boolean;
  /** Pre-fills the tag name, e.g. with the text the user searched for. */
  initialTag?: string;
  onClose: () => void;
  onCreate: (tagId: string, tag: string) => Promise<PartnerCustomerInventoryTag>;
  onCreated: (tag: PartnerCustomerInventoryTag) => void;
}

const getErrorInfo = (error: unknown): { status?: number; message: string } => {
  const fallback = "Failed to create tag. Please try again.";
  if (error && typeof error === "object") {
    const err = error as {
      response?: { status?: number; data?: { message?: string } | string };
      message?: string;
    };
    const data = err.response?.data;
    const message =
      (typeof data === "object" && data?.message) ||
      (typeof data === "string" && data) ||
      err.message ||
      fallback;
    return { status: err.response?.status, message };
  }
  return { message: fallback };
};

export default function CreateInventoryTagPopup({
  open,
  initialTag = "",
  onClose,
  onCreate,
  onCreated,
}: CreateInventoryTagPopupProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tag, setTag] = useState("");
  const [tagError, setTagError] = useState("");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setTag(initialTag.slice(0, PARTNER_TAG_NAME_MAX_LENGTH));
    setTagError("");
    setFormError("");
  }, [open, initialTag]);

  const trimmedTag = tag.trim();
  // The tag code is never typed; it is derived from the name.
  const derivedTagId = partnerTagIdFromName(tag);
  const tagIdTruncated =
    trimmedTag.replace(/\s+/g, "_").length > PARTNER_TAG_ID_MAX_LENGTH;
  const tagTooLong = trimmedTag.length > PARTNER_TAG_NAME_MAX_LENGTH;
  const canSubmit = !!trimmedTag && !!derivedTagId && !tagTooLong;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!canSubmit || isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setTagError("");
    setFormError("");
    try {
      const created = await onCreate(derivedTagId, trimmedTag);
      onCreated(created);
    } catch (error: unknown) {
      const { status, message } = getErrorInfo(error);
      if (status === 409) {
        setTagError(message);
      } else {
        setFormError(message);
      }
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: { xs: 0, sm: "16px" },
            overflow: "hidden",
            boxShadow: {
              xs: "none",
              sm: "0 20px 40px rgba(0, 0, 0, 0.15)",
            },
            background: `linear-gradient(135deg, ${theme.palette.customColors.white} 0%, ${theme.palette.customColors.lightGreyColor} 100%)`,
            height: { xs: "100%", sm: "auto" },
            maxHeight: { xs: "100%", sm: "90vh" },
            width: "100%",
            m: { xs: 0, sm: 2 },
            display: "flex",
            flexDirection: "column",
            position: "relative",
          },
        },
      }}
      sx={{
        zIndex: 2100,
        "& .MuiDialog-container": {
          alignItems: { xs: "stretch", sm: "center" },
        },
      }}
    >
      <Backdrop
        open={isSubmitting}
        sx={{
          position: "absolute",
          zIndex: (muiTheme) => muiTheme.zIndex.modal + 1,
          color: theme.palette.customColors.white,
          borderRadius: { xs: 0, sm: "16px" },
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
          maxHeight: { xs: "100%", sm: "90vh" },
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
            flexShrink: 0,
          }}
        >
          <Box minWidth={0}>
            <CustomTypography variant="heading4" fontWeight={700}>
              Add Tag
            </CustomTypography>
            <CustomTypography variant="body3" color="text.secondary">
              Create a partner tag for this customer
            </CustomTypography>
          </Box>
          <IconButton onClick={handleClose} disabled={isSubmitting}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent
          sx={{
            p: { xs: 2, sm: 3 },
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
          }}
        >
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <CustomTypography variant="body2" fontWeight={600}>
                TAG NAME
              </CustomTypography>
              <Box sx={{ mt: 1 }}>
                <CustomTextField
                  value={tag}
                  onChange={(e) => {
                    setTag(e.target.value);
                    setTagError("");
                  }}
                  width="100%"
                  placeholder="e.g. Black Hoodie"
                  disabled={isSubmitting}
                  error={!!tagError || tagTooLong}
                  helperText={
                    tagError ||
                    (tagTooLong
                      ? `Max ${PARTNER_TAG_NAME_MAX_LENGTH} characters.`
                      : undefined)
                  }
                />
              </Box>
              <Box
                sx={{
                  mt: 1,
                  px: 1.25,
                  py: 0.75,
                  borderRadius: 2,
                  border: `1px dashed ${theme.palette.customColors.greyBorder}`,
                  backgroundColor: theme.palette.customColors.white,
                  minWidth: 0,
                }}
              >
                <CustomTypography variant="body3" color="text.secondary">
                  Tag ID
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  sx={{ display: "block", wordBreak: "break-all" }}
                  color={derivedTagId ? undefined : "text.secondary"}
                >
                  {derivedTagId || "Generated from the tag name"}
                </CustomTypography>
                <CustomTypography
                  variant="body3"
                  color="text.secondary"
                  sx={{ display: "block" }}
                >
                  {tagIdTruncated
                    ? `Spaces become "_". Shortened to ${PARTNER_TAG_ID_MAX_LENGTH} characters. Cannot be changed later.`
                    : 'Spaces become "_". Cannot be changed later.'}
                </CustomTypography>
              </Box>
            </Grid2>

            {formError && (
              <Grid2 size={12}>
                <CustomTypography
                  variant="body3"
                  sx={{ display: "block", color: theme.palette.error.main }}
                >
                  {formError}
                </CustomTypography>
              </Grid2>
            )}

            <Grid2
              size={12}
              display="flex"
              justifyContent="flex-end"
              gap={1.5}
              sx={{ pt: 1 }}
            >
              <AppButton
                label="Cancel"
                onClick={handleClose}
                variant="grayscale"
                disabled={isSubmitting}
              />
              <AppButton
                label="Add Tag"
                onClick={() => {
                  void handleSubmit();
                }}
                variant="primary"
                disabled={!canSubmit || isSubmitting}
              />
            </Grid2>
          </Grid2>
        </DialogContent>
      </Box>
    </Dialog>
  );
}
